// The client side of /api/affiliations (BUILD-BRIEF §4, owner: people agent).
//
// `confirmAffiliation` is the Confirm button on the person brief: it PATCHes `status` to
// `held`, which is the operator taking responsibility for a link an import proposed. There is
// no separate "confirm" verb on the server, because holding a claim IS setting its status —
// inventing a second endpoint for it would give the record two ways to mean ▪.
import { request, qs } from './client.js';
import type { Affiliation, AffiliationSource, AffiliationStatus, AffiliationView, ListResponse } from '../../shared/types.js';

export interface AffiliationInput {
  person_id: number;
  organisation_id: number;
  role_title?: string | null;
  started_on?: string | null;
  ended_on?: string | null;
  is_current?: boolean;
  status?: AffiliationStatus;
  source_kind?: AffiliationSource | null;
  source_url?: string | null;
  source_note?: string | null;
}

export const listAffiliations = (params: { person_id?: number; organisation_id?: number; status?: string }) =>
  request<ListResponse<AffiliationView>>(`/affiliations${qs(params)}`);

export const createAffiliation = (body: AffiliationInput) =>
  request<Affiliation>('/affiliations', { method: 'POST', body: JSON.stringify(body) });

export const updateAffiliation = (id: number, body: Partial<Omit<AffiliationInput, 'person_id' | 'organisation_id'>>) =>
  request<Affiliation>(`/affiliations/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

/** ◇ → ▪. The operator stands behind a link somebody else asserted. */
export const confirmAffiliation = (id: number) => updateAffiliation(id, { status: 'held' });

/** "We said this and we were wrong." The row stays; only the status moves. */
export const retractAffiliation = (id: number) => updateAffiliation(id, { status: 'retracted' });

/** A job change closes the row. The server fills `ended_on` with today when none is given. */
export const endAffiliation = (id: number, endedOn?: string) =>
  updateAffiliation(id, { is_current: false, ...(endedOn ? { ended_on: endedOn } : {}) });
